import React, { useRef, useEffect, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import Panel from '../components/ui/Panel';
import ResponsivePanelContainer from '../components/ui/ResponsivePanelContainer';
import { UniversalUIPage } from './UniversalUIPage';
import { useParticleSystemStore } from '../stores/useParticleSystemStore';
import { createMorphableCubeGeometry } from '../logic/geometry';

// This component will contain the core InstancedMesh logic
const ParticleInstances: React.FC = () => {
    const particles = useParticleSystemStore(state => state.particles);
    const { baseSizeX, baseSizeY, baseDepth, baseTrapezoidY, baseTrapezoidZ, sourceColor, metalness, roughness, particleCount } = useParticleSystemStore();

    const meshRef = useRef<THREE.InstancedMesh>(null!);
    
    // The animation loop
    useFrame((_, delta) => {
        if (!meshRef.current || !particles.length) return;
        
        const tempMatrix = new THREE.Matrix4();
        const lerpFactor = Math.min(delta * 5, 1); // Adjust speed here. delta * 5 means it takes ~0.2s

        for (let i = 0; i < particles.length; i++) {
            const p = particles[i];

            // Animate current values towards target values
            p.currentPosition.lerp(p.targetPosition, lerpFactor);
            p.currentOrientation.slerp(p.targetOrientation, lerpFactor);
            p.currentScale.lerp(p.targetScale, lerpFactor);
            
            // Update the instance matrix with the newly animated values
            tempMatrix.compose(p.currentPosition, p.currentOrientation, p.currentScale);
            meshRef.current.setMatrixAt(i, tempMatrix);
        }
        
        // This is crucial for dynamically changing the number of instances!
        meshRef.current.count = particles.length;
        // Tell three.js that the instance matrix has changed
        meshRef.current.instanceMatrix.needsUpdate = true;
    });

    // Re-create the base geometry whenever the base shape parameters change
    const baseGeometry = useMemo(() => {
        return createMorphableCubeGeometry({
            sizeX: baseSizeX,
            sizeY: baseSizeY,
            depth: baseDepth,
            trapezoidY: baseTrapezoidY,
            trapezoidZ: baseTrapezoidZ,
        });
    }, [baseSizeX, baseSizeY, baseDepth, baseTrapezoidY, baseTrapezoidZ]);

    // We use our new factory to create the geometry for the instanced mesh
    return (
        <instancedMesh ref={meshRef} args={[baseGeometry, undefined, particleCount]}>
<meshStandardMaterial 
    color={sourceColor} 
    metalness={metalness} 
    roughness={roughness}
    onBeforeCompile={(shader) => {
        // We replace a placeholder in the original shader with our new, corrected code
        shader.vertexShader = shader.vertexShader.replace(
            '#include <beginnormal_vertex>',
            `
            #include <beginnormal_vertex>

            // Patched code to correct normals for non-uniform scaling.
            // This is done by transforming the normal by the inverse transpose of the instance matrix.
            mat3 inverseTransposeInstanceMatrix = transpose(inverse(mat3(instanceMatrix)));
            objectNormal = inverseTransposeInstanceMatrix * objectNormal;
            `
        );
    }}
/>
        </instancedMesh>
    );
};


const ParticleSystemCanvas: React.FC = () => {
    const init = useParticleSystemStore(state => state.init);

    // On mount, trigger the initial layout calculation
    useEffect(() => {
        init();
    }, [init]);

    return (
        <Canvas 
            camera={{ position: [0, 5, 12], fov: 60 }}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            shadows // Enable shadows
        >
            {/* Improved Lighting Setup */}
            <ambientLight intensity={0.1} />
            <directionalLight 
                position={[5, 10, 8]} 
                intensity={2.5} 
                castShadow 
                shadow-mapSize-width={2048}
                shadow-mapSize-height={2048}
            />
            <pointLight position={[-10, -5, -10]} intensity={0.5} color="#8b5cf6" />
            <pointLight position={[0, -5, 0]} intensity={0.3} />

            <ParticleInstances />
            <OrbitControls />
        </Canvas>
    );
};


const ParticleSystemPage: React.FC = () => {
    const meshRef = useRef<THREE.InstancedMesh>(null!); // The CPU page doesn't use this for controls, but the UI component requires it.
    // Basic component structure
    return (
        <div style={{ position: 'relative', width: '100vw', height: '100vh', background: '#101018' }}>
            {/* The 3D canvas will go here */}
            <ParticleSystemCanvas />
            
            <ResponsivePanelContainer>
                <Panel style={{ width: '100%', height: '100%', overflowY: 'auto' }}>
                    <UniversalUIPage yamlFile="particle-system-controls.yaml" store={useParticleSystemStore as any} meshRef={meshRef} />
                </Panel>
            </ResponsivePanelContainer>
        </div>
    );
};

export default ParticleSystemPage; 